import { useState } from 'react';
import { BarChart3, Layers, Loader2, Users } from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SegmentedTabBar } from '@/components/ui/segmented-tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type PostRange = 'weekly' | 'monthly' | 'yearly';

type ActivityKind =
  | 'ALL'
  | 'GENERAL'
  | 'LEAD'
  | 'THANK_YOU'
  | 'TESTIMONIAL'
  | 'MEETUP'
  | 'POLL'
  | 'INFORMATIVE';

type ActivityPoint = { date: string; posts: number };

type AggregatorStats = {
  childCommunities: number;
  joinedMembers: number;
  nestedAggregators: number;
  postActivity: Record<ActivityKind, Record<PostRange, ActivityPoint[]>>;
  coverage: { label: string; value: number; max: number }[];
};

const ACTIVITY_TABS: { value: ActivityKind; label: string; noun: string }[] = [
  { value: 'ALL', label: 'All', noun: 'Posts' },
  { value: 'GENERAL', label: 'General', noun: 'General posts' },
  { value: 'LEAD', label: 'Lead', noun: 'Leads' },
  { value: 'THANK_YOU', label: 'Thank you', noun: 'Thank you notes' },
  { value: 'TESTIMONIAL', label: 'Testimonial', noun: 'Testimonials' },
  { value: 'MEETUP', label: 'Meetup', noun: 'Meetups' },
  { value: 'POLL', label: 'Poll', noun: 'Polls' },
  { value: 'INFORMATIVE', label: 'Informative', noun: 'Informative posts' },
];

const RANGE_DETAIL: Record<PostRange, string> = {
  weekly: 'in the last 7 days',
  monthly: 'each day this month',
  yearly: 'each month this year',
};

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data as AggregatorStats);

function formatCount(value: number): string {
  return value.toLocaleString();
}

function postAxisTicks(points: ActivityPoint[]): number[] {
  const peak = Math.max(1, ...points.map((point) => point.posts));
  if (peak <= 6) {
    return Array.from({ length: peak + 1 }, (_, value) => value);
  }
  const step = Math.ceil(peak / 4);
  const ticks: number[] = [];
  for (let value = 0; value <= peak; value += step) ticks.push(value);
  if (ticks[ticks.length - 1] !== peak) ticks.push(peak);
  return ticks;
}

function formatActivityDate(value: string, range: PostRange): string {
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  if (range === 'yearly') {
    return date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function PostActivityTooltip({
  active,
  payload,
  label,
  range,
  noun,
}: {
  active?: boolean;
  payload?: { value?: number }[];
  label?: string;
  range: PostRange;
  noun: string;
}) {
  if (!active || !payload?.length || !label) return null;
  const posts = payload[0]?.value ?? 0;

  return (
    <div className="rounded-lg border border-border/60 bg-background px-3 py-2 text-xs shadow-xl">
      <div className="font-medium">{formatActivityDate(label, range)}</div>
      <div className="mt-1 text-muted-foreground">
        {posts.toLocaleString()} {noun.toLowerCase()}
      </div>
    </div>
  );
}

export default function AggregatorDashboardPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const [postRange, setPostRange] = useState<PostRange>('weekly');
  const [activityKind, setActivityKind] = useState<ActivityKind>('ALL');
  const { data, isLoading, error } = useSWR<AggregatorStats>(
    communityId ? `/api/communities/${communityId}/aggregator-stats` : null,
    fetcher
  );

  const activityTab = ACTIVITY_TABS.find((tab) => tab.value === activityKind) ?? ACTIVITY_TABS[0];
  const postActivity = data?.postActivity[activityKind]?.[postRange] ?? [];
  const yTicks = postAxisTicks(postActivity);
  const yMax = yTicks[yTicks.length - 1] ?? 1;
  const widePostChart = postRange !== 'weekly';

  return (
    <div className="p-4 lg:p-8 space-y-8 max-w-6xl mx-auto">
      <div className="space-y-2">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          <Layers className="h-3.5 w-3.5" />
          Aggregator
        </div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold tracking-tight">
          Aggregator overview
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Live counts for this aggregator and every community under it.
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error || !data ? (
        <div className="rounded-2xl border border-border/40 bg-card/50 p-6 text-sm text-muted-foreground">
          Aggregator stats could not be loaded.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                icon: Users,
                label: 'Communities',
                value: formatCount(data.childCommunities),
              },
              { icon: BarChart3, label: 'Members', value: formatCount(data.joinedMembers) },
              {
                icon: Layers,
                label: 'Aggregators',
                value: formatCount(data.nestedAggregators),
              },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-border/40 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
                  <stat.icon className="h-4 w-4" />
                  {stat.label}
                </div>
                <div className="text-3xl font-heading font-bold">{stat.value}</div>
              </div>
            ))}
          </div>

          <div className={widePostChart ? 'grid gap-6' : 'grid gap-6 lg:grid-cols-2'}>
            <Card className="min-w-0 pt-0">
              <CardHeader className="flex w-full min-w-0 flex-col items-start gap-3 space-y-0 border-b px-3 py-4 sm:px-6 sm:py-5">
                <div className="grid gap-1 text-left">
                  <CardTitle>Post activity</CardTitle>
                  <CardDescription>
                    {activityTab.noun} published {RANGE_DETAIL[postRange]}
                  </CardDescription>
                </div>
                <SegmentedTabBar
                  value={activityKind}
                  onValueChange={setActivityKind}
                  variant="compact"
                  className="w-full min-w-0"
                  listClassName="grid w-full grid-cols-4 gap-1 p-1 sm:flex sm:flex-wrap sm:gap-2 sm:p-1.5"
                  triggerClassName="h-7 w-full min-w-0 px-0.5 py-1 text-[10px] leading-none tracking-tight sm:h-auto sm:w-auto sm:px-4 sm:py-2 sm:text-sm sm:tracking-normal"
                  items={ACTIVITY_TABS.map((tab) => ({ value: tab.value, label: tab.label }))}
                />
                <Select
                  value={postRange}
                  onValueChange={(value) => setPostRange(value as PostRange)}
                >
                  <SelectTrigger
                    className="h-9 w-[148px] rounded-lg px-3"
                    aria-label="Select a range"
                  >
                    <SelectValue placeholder="Last 7 days" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="weekly" className="rounded-lg">
                      Last 7 days
                    </SelectItem>
                    <SelectItem value="monthly" className="rounded-lg">
                      This month
                    </SelectItem>
                    <SelectItem value="yearly" className="rounded-lg">
                      This year
                    </SelectItem>
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
                <div className="h-[250px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={postActivity} margin={{ left: 0, right: 12, top: 8 }}>
                      <defs>
                        <linearGradient id="fillPosts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                      <YAxis
                        domain={[0, yMax]}
                        ticks={yTicks}
                        allowDecimals={false}
                        allowDataOverflow={false}
                        axisLine={false}
                        tickLine={false}
                        width={yMax >= 100 ? 40 : 32}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        minTickGap={32}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        tickFormatter={(value: string) => formatActivityDate(value, postRange)}
                      />
                      <Tooltip
                        cursor={false}
                        content={<PostActivityTooltip range={postRange} noun={activityTab.noun} />}
                      />
                      <Area
                        dataKey="posts"
                        type="linear"
                        baseValue={0}
                        fill="url(#fillPosts)"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="rounded-2xl border border-border/40 bg-card/50 p-6">
              <h2 className="font-heading font-semibold mb-1">Tree coverage</h2>
              <p className="text-sm text-muted-foreground mb-6">Current totals across the tree</p>
              <div className="space-y-4">
                {data.coverage.map((bar) => (
                  <div key={bar.label} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span>{bar.label}</span>
                      <span className="text-muted-foreground">{formatCount(bar.value)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary/70"
                        style={{ width: `${bar.max > 0 ? (bar.value / bar.max) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
